// Copyright (c) 2026. Hardfin, Inc. All rights reserved.
// Hardfin Confidential - Restricted
import { SerializableCurrency } from '@/currency/api/types';
import { IsoCurrencyCode } from '@/currency/constants/iso';
import { CurrencyDecimal } from '@/currency/utilities/decimal';
import { fromMinorUnits, toMinorUnits } from '@/currency/utilities/math';
import { padAmountToMinorUnit } from '@/currency/utilities/serialize';
import { parseCurrencyAmount } from '@/currency/utilities/validate';
import { MilestonePhasesInput } from '@/project/components/billing-plan-constants';
import {
  MONTH_ANCHORED_FREQUENCIES,
  advanceByFrequency,
} from '@/project/utilities/billing-frequency';
import {
  type ChargeFormDateValue,
  chargeDateKey,
} from '@/project/utilities/charge-date';
import { Decimal } from 'decimal.js';
import { DateTime } from 'luxon';

/** The share of a line item its milestone phases add up to. */
const WHOLE_PERCENTAGE = 100;

export type ChargeMode = 'CONFIRMED' | 'PENDING';

export interface GenerateChargesParams {
  startOn: DateTime<boolean>;
  endOn: DateTime<boolean>;
  chargeFrequency: string;
  invoiceFrequency: string;
  invoiceMode: string;
  pricingMode: string;
  quantity: number;
  unitPrice: number;
  fixedPrice: number;
  chargeSchedule: string;
  totalOverride: number | null;
  /** The line item's single currency. Every generated charge stamps this
   *  ISO onto its unitPrice / totalPrice — a charge can never differ from
   *  its line item's currency. */
  currencyCode: IsoCurrencyCode;
  /** Plan-level mode. CONFIRMED uses real dates anchored on activationDate.
   *  PENDING emits the same period count with null dates and counter labels;
   *  each charge inherits this value. PENDING applies only to PERIODIC. */
  chargeMode: ChargeMode;
  /** The activation anchor for periodic charge generation. Required when
   *  chargeSchedule is PERIODIC. Defaults upstream to startOn. The duration
   *  end - start is preserved by day-arithmetic from activationDate. */
  activationDate: DateTime<boolean>;
  /** The user's configured milestone phases. Required when chargeSchedule is
   *  MILESTONE — one charge per entry, total_price = phase's share of the
   *  line-item total. Ignored for PERIODIC schedules. */
  milestonePhases: MilestonePhasesInput | null;
  /** Charges to preserve during regeneration (typically INVOICED). Their date
   *  ranges are subtracted from the generated periods so new charges only fill gaps. */
  preservedCharges?: ChargeFormValue[];
}

export interface ChargeFormValue {
  id: string | null;
  // Stable nano-id assigned by the backend. Only present for server-persisted
  // charges; client-only generated/split/added charges leave this null.
  // Used for deep-link focus matching from the charge listing page.
  key: string | null;
  label: string;
  pricingMode: string;
  quantity: number;
  unitPrice: SerializableCurrency | null;
  totalPrice: SerializableCurrency;
  servicePeriodStart: ChargeFormDateValue;
  servicePeriodEnd: ChargeFormDateValue;
  expectedBillableDate: ChargeFormDateValue;
  billingEligibleDate: ChargeFormDateValue;
  status: string;
  isCustomModified: boolean;
  // MANUAL on milestone-generated charges, TIME for periodic. Rides
  // through the save payload so the BE persists the correct semantics
  // (cron ignores MANUAL charges; they only flip via user action).
  triggerType: 'TIME' | 'MANUAL' | 'EVENT';
  // BE-assigned display-order counter. Populated from the server on load;
  // left at 0 for client-only rows (generated / split / added) until save
  // rewrites sequences to array_index + 1 across the whole list.
  sequence: number;
}

/**
 * Wraps a Decimal amount as the serialized currency a charge carries,
 * stamping the line item's single ISO without rounding the amount.
 */
function toChargeCurrency(
  amount: Decimal,
  currencyCode: IsoCurrencyCode
): SerializableCurrency {
  return {
    amount: padAmountToMinorUnit(amount, currencyCode),
    isoCode: currencyCode,
  };
}

/** Parses a charge's stored amount string into a Decimal. */
function toChargeAmountDecimal(amount: string): Decimal {
  const parsed = parseCurrencyAmount(amount);
  if (parsed === null) {
    throw new Error(`Charge amount "${amount}" is not a valid number`);
  }

  return parsed;
}

/** Value-compares two charge currency slots by amount and ISO code. */
function chargeCurrencyEquals(
  a: SerializableCurrency | null,
  b: SerializableCurrency | null
): boolean {
  if (a === null) return b === null;
  if (b === null) return false;
  return a.amount === b.amount && a.isoCode === b.isoCode;
}

/**
 * Generates charges based on billing configuration.
 *
 * Periodic path — per-charge amount:
 * - PER_UNIT: quantity × unit_price (each charge equals this product).
 * - FIXED: fixed_price (each charge equals the fixed price — no math).
 *   Quantity is informational, not used for amount.
 *
 * totalOverride (periodic only): when set, replaces the line item total.
 * Generated charges split that total across periods, with the final charge
 * absorbing the remainder so the schedule sums to the override.
 *
 * Milestone path — one charge per phase, total = phase's share of the
 * plan's line-item total (see generateMilestoneCharges).
 *
 * Coupled with the server's `generate` flag on the line item update, which
 * runs GenerateCharges in asset-manager's src/libs/chargeschedule/generate.go.
 * Change both together, and keep generate_test.go in step with this file's tests.
 */
export function generateCharges(
  params: GenerateChargesParams
): ChargeFormValue[] {
  switch (params.chargeSchedule) {
    case 'MILESTONE':
      return generateMilestoneCharges(params);
    case 'PERIODIC':
      return generatePeriodicCharges(params);
    default:
      return generatePeriodicCharges(params);
  }
}

/**
 * Carries forward server IDs from existing charges onto a freshly
 * generated batch. Dispatches by schedule type: periodic matches by
 * service period, milestone matches by stable phase id. See the
 * per-schedule reconcile helpers for the exact rules.
 *
 * Coupled with the server's `generate` flag, which runs ReconcileChargeIDs in
 * asset-manager's src/libs/chargeschedule/generate.go. Change both together.
 */
export function reconcileChargeIds(
  generated: ChargeFormValue[],
  existing: ChargeFormValue[],
  chargeSchedule: string
): ChargeFormValue[] {
  switch (chargeSchedule) {
    case 'MILESTONE':
      return reconcileMilestoneChargeIds(generated, existing);
    case 'PERIODIC':
      return reconcilePeriodicChargeIds(generated, existing);
    default:
      return reconcilePeriodicChargeIds(generated, existing);
  }
}

/**
 * Splits a total evenly across periods in whole minor units, the last period
 * taking the remainder.
 *
 * Coupled with allocateTotalAcrossPeriods in asset-manager's
 * src/libs/chargeschedule/generate.go. Change both together.
 */
function allocateTotalAcrossPeriods(
  total: Decimal,
  periodCount: number,
  currencyCode: IsoCurrencyCode
): Decimal[] {
  if (periodCount === 0) return [];

  const totalUnits = toMinorUnits(total, currencyCode);
  const periodUnits = totalUnits.dividedToIntegerBy(periodCount);
  const amounts = Array.from({ length: periodCount - 1 }, () =>
    fromMinorUnits(periodUnits, currencyCode)
  );
  amounts.push(
    fromMinorUnits(
      totalUnits.minus(periodUnits.times(periodCount - 1)),
      currencyCode
    )
  );
  return amounts;
}

/**
 * Splits a total by phase percentage in whole minor units, the last phase
 * taking the remainder.
 *
 * Coupled with allocateTotalAcrossPhases in asset-manager's
 * src/libs/chargeschedule/generate.go. Change both together.
 */
function allocateTotalAcrossPhases(
  total: Decimal,
  phases: MilestonePhasesInput,
  currencyCode: IsoCurrencyCode
): Decimal[] {
  const totalUnits = toMinorUnits(total, currencyCode);
  const phaseUnits = phases.entries
    .slice(0, -1)
    .map((entry) =>
      totalUnits.times(entry.percentage).dividedToIntegerBy(WHOLE_PERCENTAGE)
    );
  const allocatedUnits = phaseUnits.reduce(
    (sum, units) => sum.plus(units),
    new CurrencyDecimal(0)
  );
  phaseUnits.push(totalUnits.minus(allocatedUnits));
  return phaseUnits.map((units) => fromMinorUnits(units, currencyCode));
}

function generatePeriodicCharges(
  params: GenerateChargesParams
): ChargeFormValue[] {
  const schedule = buildShiftedServiceSchedule(params);
  if (schedule === null) return [];

  const allPeriods = schedule.periods;
  if (
    allPeriods.length === 0 &&
    (!params.preservedCharges || params.preservedCharges.length === 0)
  )
    return [];

  const preserved = params.preservedCharges ?? [];
  const gapPeriods =
    preserved.length > 0
      ? subtractPreservedPeriods(allPeriods, preserved)
      : allPeriods;

  // FIXED: fixed_price is the per-charge amount (no math).
  // PER_UNIT: quantity × unit_price.
  // totalOverride replaces the line item total and is distributed across
  // generated charges. Preserved charges keep their existing totals; any
  // remaining override amount is allocated to the gap periods.
  const unitPrice = new CurrencyDecimal(params.unitPrice);
  const baseChargePerPeriod =
    params.pricingMode === 'PER_UNIT'
      ? unitPrice.times(params.quantity)
      : new CurrencyDecimal(params.fixedPrice);
  const preservedTotal = preserved.reduce(
    (sum, charge) => sum.plus(toChargeAmountDecimal(charge.totalPrice.amount)),
    new CurrencyDecimal(0)
  );
  const lineItemTotal =
    params.totalOverride !== null
      ? new CurrencyDecimal(params.totalOverride)
      : baseChargePerPeriod.times(allPeriods.length);
  const generatedTotal = lineItemTotal.minus(preservedTotal);
  const generatedChargeTotals =
    params.totalOverride !== null
      ? allocateTotalAcrossPeriods(
          generatedTotal,
          gapPeriods.length,
          params.currencyCode
        )
      : gapPeriods.map(() => baseChargePerPeriod);

  const isPending = params.chargeMode === 'PENDING';
  let chargeCounter = 0;
  const generated = gapPeriods.map((period): ChargeFormValue => {
    chargeCounter++;
    // PENDING charges have no real dates: the activation step (future
    // work) will fill them in using the saved activation_date. Counter
    // labels stand in for "January 2026" / "Q1 2026" / etc. until then.
    const billableDate = isPending
      ? null
      : calculateExpectedBillableDate(period, schedule, params);

    // Line item quantity is preserved on both PER_UNIT and FIXED charges; in
    // FIXED mode it is informational (not used for amount math — total_price
    // is the authoritative billed amount). See billing-conceptual-model.md.
    const chargeQuantity = params.quantity;
    // The unit_price field is the PER_UNIT operand. Populated only when:
    //   - pricing mode is PER_UNIT
    //   - a positive quantity exists (guards the qty=0 case)
    //   - no total_override is in effect — when override is set, the line-item
    //     total is distributed across charges, so keeping
    //     unit_price populated would trip the BE's strict
    //     unit_price × qty == total_price check and block every save with
    //     no in-app workaround (the FE can't magically reconcile an
    //     arbitrary override with an integer quantity × per-unit rate).
    // FIXED charges and PER_UNIT-with-override charges both ship null
    // unit_price; the row hides the qty × unit breakdown in that case
    // and just shows total_price.
    const chargeUnitPrice =
      params.pricingMode === 'PER_UNIT' &&
      params.quantity > 0 &&
      params.totalOverride === null
        ? toChargeCurrency(unitPrice, params.currencyCode)
        : null;
    const chargeTotal = toChargeCurrency(
      generatedChargeTotals[chargeCounter - 1],
      params.currencyCode
    );

    return {
      id: `gen-${String(chargeCounter)}`,
      key: null,
      label: isPending
        ? generatePendingLabel(params.chargeFrequency, chargeCounter)
        : generateLabel(period.start, params.chargeFrequency),
      pricingMode: params.pricingMode,
      quantity: chargeQuantity,
      unitPrice: chargeUnitPrice,
      totalPrice: chargeTotal,
      servicePeriodStart: isPending ? null : period.start,
      servicePeriodEnd: isPending ? null : period.end,
      expectedBillableDate: billableDate,
      billingEligibleDate: null,
      status: 'PLANNED',
      isCustomModified: false,
      triggerType: 'TIME',
      // Client-only row; sequence is assigned on save from array index.
      sequence: 0,
    };
  });

  if (preserved.length === 0) return generated;

  // Merge preserved + generated, sort by service period start. Dated
  // charges (CONFIRMED) sort by their ISO date; null-dated charges
  // (PENDING) sort to the end of the list so the audit timeline reads
  // chronologically with placeholder rows last.
  return [...preserved, ...generated].sort((a, b) => {
    const aStart = chargeDateKey(a.servicePeriodStart);
    const bStart = chargeDateKey(b.servicePeriodStart);
    if (aStart === null && bStart === null) return 0;
    if (aStart === null) return 1;
    if (bStart === null) return -1;
    return aStart.localeCompare(bStart);
  });
}

function computeMilestoneAmounts(
  phases: MilestonePhasesInput,
  lineItemTotal: Decimal,
  currencyCode: IsoCurrencyCode
): Decimal[] {
  return allocateTotalAcrossPhases(lineItemTotal, phases, currencyCode);
}

function generateMilestoneCharges(
  params: GenerateChargesParams
): ChargeFormValue[] {
  const phases = params.milestonePhases;
  if (phases === null) return [];

  // Milestone charges split the full line-item total across the phases. A
  // milestone plan never carries a total_override (the BE forbids it), so the
  // total is always the pricing-derived amount.
  const lineUnit = new CurrencyDecimal(params.unitPrice);
  const lineItemTotal =
    params.pricingMode === 'PER_UNIT'
      ? lineUnit.times(params.quantity)
      : new CurrencyDecimal(params.fixedPrice);

  // PER_UNIT is the common path: derive a per-phase unit_price so each row
  // reads "qty × unit_price = total" cleanly, the BE's strict
  // unit_price × qty == total_price check passes, and the totals sum exactly
  // to the line item total.
  //
  // Algorithm: per-phase unit_price = percentage × line_unit, floored.
  // The last phase's unit_price = line_unit − sum(other phase units), so
  // the remainder absorbed at the unit level propagates cleanly into
  // totals (total = qty × unit is always exact by construction).
  //
  // Example: qty=3, unit=$999.99, 40/10/10/30/10
  //   per-phase units:  [$399.99, $99.99, $99.99, $299.99, $100.03]
  //   per-phase totals: [$1199.97, $299.97, $299.97, $899.97, $300.09]  sum $2999.97
  const canDerivePerPhaseUnit =
    params.pricingMode === 'PER_UNIT' &&
    params.quantity > 0 &&
    params.unitPrice > 0;

  // Milestone phases always generate with NULL service period and
  // expected_billable_date — the user fills these in via inline edit on
  // the row (or leaves them blank). Service periods are optional on every
  // charge regardless of status.
  if (canDerivePerPhaseUnit) {
    const phaseUnits = allocateTotalAcrossPhases(
      lineUnit,
      phases,
      params.currencyCode
    );
    return phases.entries.map((entry, entryIndex): ChargeFormValue => {
      const unit = phaseUnits[entryIndex];
      const total = unit.times(params.quantity);
      return {
        id: `gen-m-${String(entryIndex + 1)}`,
        key: null,
        label: entry.name,
        pricingMode: params.pricingMode,
        quantity: params.quantity,
        unitPrice: toChargeCurrency(unit, params.currencyCode),
        totalPrice: toChargeCurrency(total, params.currencyCode),
        servicePeriodStart: null,
        servicePeriodEnd: null,
        expectedBillableDate: null,
        billingEligibleDate: null,
        status: 'PLANNED',
        isCustomModified: false,
        triggerType: 'MANUAL',
        sequence: 0,
      };
    });
  }

  // FIXED line-item pricing. unit_price stays null — there is no clean
  // per-unit rate to derive, and the BE's strict × rule would reject a
  // rounded one. total_price is the computed phase amount, with the last
  // phase absorbing the remainder via the amount itself.
  const amounts = computeMilestoneAmounts(
    phases,
    lineItemTotal,
    params.currencyCode
  );
  return phases.entries.map((entry, entryIndex): ChargeFormValue => {
    return {
      id: `gen-m-${String(entryIndex + 1)}`,
      key: null,
      label: entry.name,
      pricingMode: params.pricingMode,
      quantity: params.quantity,
      unitPrice: null,
      totalPrice: toChargeCurrency(amounts[entryIndex], params.currencyCode),
      servicePeriodStart: null,
      servicePeriodEnd: null,
      expectedBillableDate: null,
      billingEligibleDate: null,
      status: 'PLANNED',
      isCustomModified: false,
      triggerType: 'MANUAL',
      sequence: 0,
    };
  });
}

interface ServicePeriod {
  start: DateTime;
  end: DateTime;
}

interface ChargeScheduleDateInput {
  startOn: DateTime<boolean>;
  endOn: DateTime<boolean>;
  activationDate: DateTime<boolean>;
  chargeFrequency: string;
}

interface BillableDateInput {
  invoiceFrequency: string;
  invoiceMode: string;
}

interface ShiftedServiceSchedule {
  serviceStart: DateTime;
  serviceEnd: DateTime;
  periods: ServicePeriod[];
}

function buildShiftedServiceSchedule(
  params: ChargeScheduleDateInput
): ShiftedServiceSchedule | null {
  if (
    !params.startOn.isValid ||
    !params.endOn.isValid ||
    !params.activationDate.isValid
  ) {
    return null;
  }
  if (params.endOn < params.startOn) return null;

  // Duration is preserved by day-count. April 1 → March 31 (365 days
  // exclusive) with activation April 15 yields April 15 + 365 = April
  // 14 next year. Day arithmetic always produces a legal calendar
  // date — leap-year boundaries (e.g. Mar 1 2023 + 365 days = Feb 29
  // 2024 because the year being crossed contains the leap day) clamp
  // naturally without explicit day-of-month snapping.
  const durationDays = Math.round(
    params.endOn.diff(params.startOn, 'days').days
  );
  const serviceStart = params.activationDate;
  const serviceEnd = serviceStart.plus({ days: durationDays });
  return {
    serviceStart,
    serviceEnd,
    periods: computeServicePeriods(
      serviceStart,
      serviceEnd,
      params.chargeFrequency
    ),
  };
}

function calculateExpectedBillableDate(
  period: ServicePeriod,
  schedule: ShiftedServiceSchedule,
  params: BillableDateInput
): DateTime | null {
  if (params.invoiceFrequency === 'ONCE') {
    return params.invoiceMode === 'ADVANCE'
      ? schedule.serviceStart
      : schedule.serviceEnd;
  }

  if (params.invoiceMode === 'ADVANCE') {
    return findBillingPeriodStart(
      period.start,
      schedule.serviceStart,
      params.invoiceFrequency
    );
  }
  return findBillingPeriodEnd(
    period.start,
    schedule.serviceStart,
    params.invoiceFrequency
  );
}

function computeServicePeriods(
  start: DateTime,
  end: DateTime,
  frequency: string
): ServicePeriod[] {
  const freq = frequency.toUpperCase();
  // For ONCE frequency, just return the whole range
  if (freq === 'ONCE') {
    return [{ start, end }];
  }

  const monthAnchored = MONTH_ANCHORED_FREQUENCIES.has(freq);
  // AnchorDay is the original day-of-month the schedule walks from.
  // Pre-clamp every clean advance preserves it on `current`; once a
  // clamp happens, `current` shifts to the day after the clamped end
  // (always day 1) and stays there for the remainder of the schedule.
  // The `atAnchor` guard makes it explicit that clamp detection only
  // fires while we still hold the anchor — see the BE companion,
  // ComputeServicePeriods in src/libs/chargeschedule/cascade.go.
  const anchorDay = start.day;
  const periods: ServicePeriod[] = [];
  let current = start;

  while (current <= end) {
    const next = advanceByFrequency(current, freq);
    const atAnchor = current.day === anchorDay;
    const clamped = monthAnchored && atAnchor && next.day !== anchorDay;
    const naturalEnd = clamped ? next : next.minus({ days: 1 });
    const periodEnd = naturalEnd > end ? end : naturalEnd;
    periods.push({ start: current, end: periodEnd });
    current = periodEnd.plus({ days: 1 });
  }

  return periods;
}

/**
 * Subtracts date ranges covered by preserved charges from the generated periods.
 * Returns only the remaining gap periods where new charges should be generated.
 */
function subtractPreservedPeriods(
  periods: ServicePeriod[],
  preserved: ChargeFormValue[]
): ServicePeriod[] {
  if (preserved.length === 0) return periods;

  // Build sorted blocked intervals from preserved charges
  const hasServicePeriod = (
    c: ChargeFormValue
  ): c is ChargeFormValue & {
    servicePeriodStart: DateTime;
    servicePeriodEnd: DateTime;
  } => c.servicePeriodStart !== null && c.servicePeriodEnd !== null;

  const blocked = preserved
    .filter(hasServicePeriod)
    .map((c) => ({
      start: c.servicePeriodStart,
      end: c.servicePeriodEnd,
    }))
    .sort((a, b) => a.start.toMillis() - b.start.toMillis());

  const result: ServicePeriod[] = [];

  for (const period of periods) {
    let remaining: ServicePeriod[] = [period];

    for (const block of blocked) {
      const next: ServicePeriod[] = [];
      for (const r of remaining) {
        // No overlap
        if (r.end < block.start || r.start > block.end) {
          next.push(r);
          continue;
        }
        // Partial overlap at start — keep the portion after the block
        if (r.start >= block.start && r.end > block.end) {
          const trimmed = { start: block.end.plus({ days: 1 }), end: r.end };
          if (trimmed.start <= trimmed.end) next.push(trimmed);
          continue;
        }
        // Partial overlap at end — keep the portion before the block
        if (r.start < block.start && r.end <= block.end) {
          const trimmed = {
            start: r.start,
            end: block.start.minus({ days: 1 }),
          };
          if (trimmed.start <= trimmed.end) next.push(trimmed);
          continue;
        }
        // Block is in the middle — split into two
        if (r.start < block.start && r.end > block.end) {
          const before = {
            start: r.start,
            end: block.start.minus({ days: 1 }),
          };
          const after = { start: block.end.plus({ days: 1 }), end: r.end };
          if (before.start <= before.end) next.push(before);
          if (after.start <= after.end) next.push(after);
          continue;
        }
        // Full overlap — discard
      }
      remaining = next;
    }

    result.push(...remaining);
  }

  return result;
}

function findBillingPeriodStart(
  chargeDate: DateTime,
  contractStart: DateTime,
  invoiceFrequency: string
): DateTime {
  let current = contractStart;
  while (current <= chargeDate) {
    const next = advanceByFrequency(current, invoiceFrequency);
    if (next > chargeDate) return current;
    current = next;
  }
  return current;
}

function findBillingPeriodEnd(
  chargeDate: DateTime,
  contractStart: DateTime,
  invoiceFrequency: string
): DateTime {
  let current = contractStart;
  while (current <= chargeDate) {
    const next = advanceByFrequency(current, invoiceFrequency);
    if (next > chargeDate) return next.minus({ days: 1 });
    current = next;
  }
  return current.minus({ days: 1 });
}

/**
 * PENDING charge label: counter-based by frequency. Used when the plan
 * is in PENDING mode and charges have no real dates yet — "Month 1",
 * "Week 2", "Year 1", etc. Index is 1-based.
 */
function generatePendingLabel(frequency: string, index: number): string {
  const freq = frequency.toUpperCase();
  switch (freq) {
    case 'DAILY':
      return `Day ${String(index)}`;
    case 'WEEKLY':
    case 'BIWEEKLY':
      return `Week ${String(index)}`;
    case 'MONTHLY':
      return `Month ${String(index)}`;
    case 'QUARTERLY':
      return `Quarter ${String(index)}`;
    case 'SEMIANNUALLY':
      return `Half ${String(index)}`;
    case 'ANNUALLY':
    case 'BIENNIALLY':
    case 'TRIENNIALLY':
    case 'QUADRENNIALLY':
    case 'QUINQUENNIALLY':
      return `Year ${String(index)}`;
    case 'ONCE':
      return 'Pending activation';
    default:
      return `Period ${String(index)}`;
  }
}

/**
 * Per-charge cascade preview: takes the line item's contract dates,
 * an activation date, the charge schedule config, and an ordered list
 * of existing PENDING charges, and returns the same charges with their
 * service period + bill date + label rewritten for the new activation
 * anchor. Pure function — no I/O, no mutations of the input list.
 *
 * Preserves identity (`id`, `key`), amounts (`totalPrice`, `unitPrice`,
 * `pricingMode`, `quantity`), and per-charge metadata (`status`,
 * `triggerType`, `isCustomModified`, `sequence`). Only the date triple
 * and the label move.
 *
 * The activation dialog uses this so it can preview *and* submit the
 * date+label updates: when the user picks an activation date the BE
 * cascades the dates anyway (CascadeActivationDates), but only the FE
 * knows how to render `generateLabel` for a given period — so the FE
 * computes both date and label here and submits the resulting charge
 * list to the BE. The BE then writes those date+label values verbatim,
 * keeping FE and BE label rendering in lockstep.
 *
 * Returns null when the contract dates / activation date / frequency
 * inputs are insufficient to produce a period count matching the input
 * charge list. Caller should fall back to the BE-only cascade in that
 * case.
 */
export interface CascadeChargeDatesParams {
  startOn: DateTime<boolean>;
  endOn: DateTime<boolean>;
  activationDate: DateTime<boolean>;
  chargeFrequency: string;
  invoiceFrequency: string;
  invoiceMode: string;
  charges: ChargeFormValue[];
}

export function cascadeChargeDates(
  params: CascadeChargeDatesParams
): ChargeFormValue[] | null {
  if (params.charges.length === 0) {
    return [];
  }
  const schedule = buildShiftedServiceSchedule(params);
  if (schedule === null) return null;

  const periods = schedule.periods;
  // Caller's contract: one charge per generated period. If they don't
  // line up, the dialog can't safely positionally rewrite — bail out
  // and let the BE do its own cascade.
  if (periods.length !== params.charges.length) {
    return null;
  }

  return params.charges.map((charge, index) => {
    const period = periods[index];
    const billableDate = calculateExpectedBillableDate(
      period,
      schedule,
      params
    );
    return {
      ...charge,
      label: generateLabel(period.start, params.chargeFrequency),
      servicePeriodStart: period.start,
      servicePeriodEnd: period.end,
      expectedBillableDate: billableDate,
    };
  });
}

function generateLabel(periodStart: DateTime, frequency: string): string {
  switch (frequency.toUpperCase()) {
    case 'DAILY':
      return periodStart.toFormat('LLL d, yyyy');
    case 'WEEKLY':
    case 'BIWEEKLY':
      return `Week of ${periodStart.toFormat('LLL d, yyyy')}`;
    case 'MONTHLY':
      return periodStart.toFormat('LLLL yyyy');
    case 'QUARTERLY': {
      const quarter = Math.ceil(periodStart.month / 3);
      return `Q${String(quarter)} ${String(periodStart.year)}`;
    }
    case 'SEMIANNUALLY': {
      const half = periodStart.month <= 6 ? 'H1' : 'H2';
      return `${half} ${String(periodStart.year)}`;
    }
    case 'ANNUALLY':
    case 'BIENNIALLY':
    case 'TRIENNIALLY':
    case 'QUADRENNIALLY':
    case 'QUINQUENNIALLY':
      return String(periodStart.year);
    case 'ONCE':
      return 'Full contract';
    default:
      return periodStart.toFormat('LLLL yyyy');
  }
}

/**
 * Splits a charge at the given index into two halves by quantity.
 * Returns a new array — does not mutate the input.
 *
 * The split halves keep the original's clean label; the "(n/total)"
 * decoration is computed at display time from the sibling grouping
 * (see decorateChargeLabel) rather than baked into the stored value.
 * That keeps the stored label canonical — one user-entered name per
 * phase / period, however many times it gets split or merged later.
 */
export function splitCharge(
  charges: ChargeFormValue[],
  index: number
): ChargeFormValue[] {
  const charge = charges[index];
  if (charge.quantity < 2) return charges;

  const firstQty = Math.floor(charge.quantity / 2);
  const secondQty = charge.quantity - firstQty;
  const ts = String(Date.now());

  // PER_UNIT halves keep the same unit_price and recompute total as
  // qty × unit_price so the BE strict check still holds.
  //
  // FIXED / milestone / override charges ship unit_price: null, so we
  // split the stored total proportionally by quantity instead. The
  // second half absorbs the remainder so the two halves sum to the original
  // total exactly — a naive `parseFloat(unitPrice ?? '0')` path would
  // zero out both halves here and silently destroy the charge amount.
  // Both halves inherit the original charge's single currency.
  const currencyCode = charge.totalPrice.isoCode;
  let firstTotal: SerializableCurrency;
  let secondTotal: SerializableCurrency;
  if (charge.unitPrice !== null) {
    const unitPrice = toChargeAmountDecimal(charge.unitPrice.amount);
    firstTotal = toChargeCurrency(unitPrice.times(firstQty), currencyCode);
    secondTotal = toChargeCurrency(unitPrice.times(secondQty), currencyCode);
  } else {
    const originalTotal = toChargeAmountDecimal(charge.totalPrice.amount);
    // The first part gets the whole minor units its quantity earns, and the
    // second part takes the rest of the total.
    const firstAmount = fromMinorUnits(
      toMinorUnits(originalTotal, currencyCode)
        .times(firstQty)
        .dividedToIntegerBy(charge.quantity),
      currencyCode
    );
    firstTotal = toChargeCurrency(firstAmount, currencyCode);
    secondTotal = toChargeCurrency(
      originalTotal.minus(firstAmount),
      currencyCode
    );
  }

  // Splits drop the server-assigned key — both halves are now client-only
  // until the next save reconciles them with the backend.
  // The server refuses a new charge that is not planned.
  const first: ChargeFormValue = {
    ...charge,
    id: `split-${ts}-a`,
    key: null,
    quantity: firstQty,
    totalPrice: firstTotal,
    status: 'PLANNED',
    isCustomModified: true,
  };
  const second: ChargeFormValue = {
    ...charge,
    id: `split-${ts}-b`,
    key: null,
    quantity: secondQty,
    totalPrice: secondTotal,
    status: 'PLANNED',
    isCustomModified: true,
  };

  const next = [...charges];
  next.splice(index, 1, first, second);
  return next;
}

/**
 * Checks if two charges have identical business fields (ignoring ID).
 */
export function chargeFieldsMatch(
  a: ChargeFormValue,
  b: ChargeFormValue
): boolean {
  return (
    a.label === b.label &&
    a.pricingMode === b.pricingMode &&
    a.quantity === b.quantity &&
    chargeCurrencyEquals(a.unitPrice, b.unitPrice) &&
    chargeCurrencyEquals(a.totalPrice, b.totalPrice) &&
    chargeDateKey(a.servicePeriodStart) ===
      chargeDateKey(b.servicePeriodStart) &&
    chargeDateKey(a.servicePeriodEnd) === chargeDateKey(b.servicePeriodEnd) &&
    chargeDateKey(a.expectedBillableDate) ===
      chargeDateKey(b.expectedBillableDate) &&
    chargeDateKey(a.billingEligibleDate) ===
      chargeDateKey(b.billingEligibleDate) &&
    a.status === b.status &&
    a.isCustomModified === b.isCustomModified
  );
}

/**
 * Carries forward server IDs from existing charges where the service period
 * matches a generated charge. This ensures regenerating an unchanged schedule
 * produces a no-op on save (acts as a "reset" to the default state).
 *
 * Periodic reconciliation — match by service period dates, the canonical
 * identifier for a periodic charge. Milestone plans must use
 * reconcileMilestoneChargeIds instead; every milestone charge shares the
 * same line-item period, so this period-based match would collapse them
 * into a single server id and the rest would look "new".
 */
function reconcilePeriodicChargeIds(
  generated: ChargeFormValue[],
  existing: ChargeFormValue[]
): ChargeFormValue[] {
  // Build a lookup of existing charges by service period key
  const existingByPeriod = new Map<string, ChargeFormValue>();
  for (const charge of existing) {
    const key = `${chargeDateKey(charge.servicePeriodStart) ?? ''}|${chargeDateKey(charge.servicePeriodEnd) ?? ''}`;
    // Only map the first match per period (avoid duplicates from splits)
    if (!existingByPeriod.has(key)) {
      existingByPeriod.set(key, charge);
    }
  }

  const usedIds = new Set<string>();
  return generated.map((gen) => {
    // Preserved (INVOICED) charges already have correct IDs from generateCharges
    if (gen.status === 'INVOICED') return gen;

    const key = `${chargeDateKey(gen.servicePeriodStart) ?? ''}|${chargeDateKey(gen.servicePeriodEnd) ?? ''}`;
    const match = existingByPeriod.get(key);
    if (match && match.id && !usedIds.has(match.id)) {
      usedIds.add(match.id);
      return { ...gen, id: match.id };
    }
    return gen;
  });
}

/**
 * Milestone reconciliation — match by ordered position. Milestone charges
 * are not linked to phases via a stable id; the phase list is
 * informational on the plan and the charge list is rebuilt from scratch
 * on regenerate. Matching by position lets the BE see "in-place update"
 * deltas (same id, new values) for unchanged slots and "new row" for
 * added slots, so the audit timeline stays clean when the user rebalances
 * a plan without changing its length.
 *
 * This reconciliation runs only when Regenerate is user-enabled — the
 * drawer disables the button whenever any existing charge is INVOICED,
 * so we do not need to special-case invoiced rows here.
 */
function reconcileMilestoneChargeIds(
  generated: ChargeFormValue[],
  existing: ChargeFormValue[]
): ChargeFormValue[] {
  return generated.map((gen, index) => {
    // Positions past the existing list are brand-new rows that the user
    // just added; leave their ids untouched so the BE assigns fresh ones.
    if (index >= existing.length) return gen;
    const match = existing[index];
    if (match.id === null) return gen;
    return { ...gen, id: match.id };
  });
}
